import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
    Package, Plus, Search, Filter, CheckCircle2, Clock, 
    AlertOctagon, FileEdit, Trash2, ExternalLink, RefreshCw, Eye, AlertCircle, FileText
} from 'lucide-react';
import { getProducts, getUserId, deleteProduct } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

const DRAFT_STORAGE_KEY = 'luxvono_seller_active_draft';

export default function SellerProductsList({ initialTab }) {
    const navigate = useNavigate();
    const location = useLocation();
    const rtl = isRTL();

    const getTabFromPath = () => {
        if (initialTab) return initialTab.toUpperCase();
        if (location.pathname.includes('/drafts')) return 'DRAFTS';
        if (location.pathname.includes('/pending')) return 'PENDING';
        if (location.pathname.includes('/published')) return 'PUBLISHED';
        if (location.pathname.includes('/rejected')) return 'REJECTED';
        return 'ALL';
    };

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(getTabFromPath()); // ALL, PUBLISHED, PENDING, REJECTED, DRAFTS
    const [searchTerm, setSearchTerm] = useState('');
    const [savedDraft, setSavedDraft] = useState(null);

    // Sync tab if URL changes
    useEffect(() => {
        setActiveTab(getTabFromPath());
    }, [location.pathname, initialTab]);

    const loadProducts = async () => {
        try {
            setLoading(true);
            const userId = getUserId() || localStorage.getItem('userId');
            const res = await getProducts(0, 100, '', '', '', userId);
            if (res && res.data) {
                const list = res.data.content || (Array.isArray(res.data) ? res.data : []);
                setProducts(list);
            }
        } catch (err) {
            console.error('Failed to load seller products:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProducts();
        // Check for saved draft
        try {
            const rawDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
            if (rawDraft) {
                const parsed = JSON.parse(rawDraft);
                if (parsed && (parsed.nameAr || parsed.nameEn || parsed.videoUrl || parsed.images?.length > 0)) {
                    setSavedDraft(parsed);
                }
            }
        } catch (_) {}
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm(rtl ? 'هل أنت متأكد من رغبتك في حذف هذا المنتج؟' : 'Are you sure you want to delete this product?')) {
            return;
        }
        try {
            await deleteProduct(id);
            setProducts(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            alert('Failed to delete product: ' + (err.message || 'Error'));
        }
    };

    const handleDiscardDraft = () => {
        if (window.confirm(rtl ? 'هل أنت متأكد من حذف هذه المسودة المحفوظة؟' : 'Are you sure you want to discard this saved draft?')) {
            localStorage.removeItem(DRAFT_STORAGE_KEY);
            setSavedDraft(null);
        }
    };

    // Filter products according to active tab
    const filteredProducts = products.filter(p => {
        const isPending = p.approvalStatus === 'PENDING' || p.approvalStatus === 'PENDING_REVIEW' || (!p.active && p.approvalStatus !== 'REJECTED');
        const isRejected = p.approvalStatus === 'REJECTED';
        const isApproved = p.approvalStatus === 'APPROVED' || (p.active && !isRejected && !isPending);

        if (activeTab === 'PUBLISHED' && !isApproved) return false;
        if (activeTab === 'PENDING' && !isPending) return false;
        if (activeTab === 'REJECTED' && !isRejected) return false;
        if (activeTab === 'DRAFTS') return false; // Handled separately

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            const matchesName = (p.name || '').toLowerCase().includes(term) || (p.nameAr || '').toLowerCase().includes(term);
            const matchesCategory = (p.categoryName || '').toLowerCase().includes(term);
            return matchesName || matchesCategory;
        }

        return true;
    });

    const publishedCount = products.filter(p => p.approvalStatus === 'APPROVED' || (p.active && p.approvalStatus !== 'REJECTED' && p.approvalStatus !== 'PENDING')).length;
    const pendingCount = products.filter(p => p.approvalStatus === 'PENDING' || p.approvalStatus === 'PENDING_REVIEW' || (!p.active && p.approvalStatus !== 'REJECTED')).length;
    const rejectedCount = products.filter(p => p.approvalStatus === 'REJECTED').length;
    const draftsCount = savedDraft ? 1 : 0;

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 22, fontWeight: 800, color: 'var(--seller-text, #111827)' }}>
                        {rtl ? 'إدارة كتالوج المنتجات الفاخرة' : 'My Product Catalog & Stock'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--seller-muted, #64748b)' }}>
                        {rtl ? 'متابعة وتعديل وإعادة إرسال منتجاتك حسب حالة المراجعة والاعتماد.' : 'Manage, edit, and track status for all your luxury marketplace items.'}
                    </p>
                </div>

                <Link 
                    to="/seller/products/new"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FE2C55', color: '#ffffff', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 4px 14px rgba(254,44,85,0.35)' }}
                >
                    <Plus size={16} />
                    <span>{rtl ? '+ نشر منتج جديد' : '+ Add Product'}</span>
                </Link>
            </div>

            {/* Status Tabs Bar (Mobile App Style Scrollable Pills) */}
            <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid var(--seller-border, #e5e7eb)', paddingBottom: 14, marginBottom: 20, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <button 
                    onClick={() => setActiveTab('ALL')}
                    style={{
                        background: activeTab === 'ALL' ? '#FE2C55' : 'var(--seller-card, #ffffff)',
                        color: activeTab === 'ALL' ? '#ffffff' : 'var(--seller-text, #4b5563)',
                        border: activeTab === 'ALL' ? '1px solid #FE2C55' : '1px solid var(--seller-border, #e5e7eb)',
                        padding: '8px 16px',
                        borderRadius: 20,
                        fontWeight: 700,
                        fontSize: 12.5,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: activeTab === 'ALL' ? '0 2px 10px rgba(254,44,85,0.35)' : 'none',
                        transition: 'all 0.2s ease'
                    }}
                >
                    {rtl ? 'كافة المنتجات' : 'All Products'} ({products.length})
                </button>

                <button 
                    onClick={() => setActiveTab('PUBLISHED')}
                    style={{
                        background: activeTab === 'PUBLISHED' ? 'rgba(16,185,129,0.15)' : 'var(--seller-card, #ffffff)',
                        color: activeTab === 'PUBLISHED' ? '#10b981' : 'var(--seller-text, #4b5563)',
                        border: activeTab === 'PUBLISHED' ? '1px solid #10b981' : '1px solid var(--seller-border, #e5e7eb)',
                        padding: '8px 16px',
                        borderRadius: 20,
                        fontWeight: 700,
                        fontSize: 12.5,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: activeTab === 'PUBLISHED' ? '0 2px 10px rgba(16,185,129,0.2)' : 'none',
                        transition: 'all 0.2s ease'
                    }}
                >
                    {rtl ? 'المعتمدة والمنشورة' : 'Published'} ({publishedCount})
                </button>

                <button 
                    onClick={() => setActiveTab('PENDING')}
                    style={{
                        background: activeTab === 'PENDING' ? 'rgba(245,158,11,0.15)' : 'var(--seller-card, #ffffff)',
                        color: activeTab === 'PENDING' ? '#d97706' : 'var(--seller-text, #4b5563)',
                        border: activeTab === 'PENDING' ? '1px solid #f59e0b' : '1px solid var(--seller-border, #e5e7eb)',
                        padding: '8px 16px',
                        borderRadius: 20,
                        fontWeight: 700,
                        fontSize: 12.5,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: activeTab === 'PENDING' ? '0 2px 10px rgba(245,158,11,0.2)' : 'none',
                        transition: 'all 0.2s ease'
                    }}
                >
                    {rtl ? 'قيد المراجعة' : 'Pending Review'} ({pendingCount})
                </button>

                <button 
                    onClick={() => setActiveTab('REJECTED')}
                    style={{
                        background: activeTab === 'REJECTED' ? 'rgba(254,44,85,0.12)' : 'var(--seller-card, #ffffff)',
                        color: activeTab === 'REJECTED' ? '#FE2C55' : 'var(--seller-text, #4b5563)',
                        border: activeTab === 'REJECTED' ? '1px solid #FE2C55' : '1px solid var(--seller-border, #e5e7eb)',
                        padding: '8px 16px',
                        borderRadius: 20,
                        fontWeight: 700,
                        fontSize: 12.5,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: activeTab === 'REJECTED' ? '0 2px 10px rgba(254,44,85,0.2)' : 'none',
                        transition: 'all 0.2s ease'
                    }}
                >
                    {rtl ? 'المرفوضة' : 'Requires Changes'} ({rejectedCount})
                </button>

                <button 
                    onClick={() => setActiveTab('DRAFTS')}
                    style={{
                        background: activeTab === 'DRAFTS' ? 'rgba(254,44,85,0.12)' : 'var(--seller-card, #ffffff)',
                        color: activeTab === 'DRAFTS' ? '#FE2C55' : 'var(--seller-text, #4b5563)',
                        border: activeTab === 'DRAFTS' ? '1px solid #FE2C55' : '1px solid var(--seller-border, #e5e7eb)',
                        padding: '8px 16px',
                        borderRadius: 20,
                        fontWeight: 700,
                        fontSize: 12.5,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: activeTab === 'DRAFTS' ? '0 2px 10px rgba(254,44,85,0.2)' : 'none',
                        transition: 'all 0.2s ease'
                    }}
                >
                    {rtl ? 'المسودات' : 'Drafts'} ({draftsCount})
                </button>
            </div>

            {/* Search Input (When not on drafts tab) */}
            {activeTab !== 'DRAFTS' && (
                <div style={{ marginBottom: 20, maxWidth: 360, position: 'relative' }}>
                    <input 
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={rtl ? 'البحث بالاسم أو القسم...' : 'Search by name or category...'}
                        style={{ width: '100%', padding: '10px 14px 10px 36px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                    />
                    <Search size={16} color="#64748b" style={{ position: 'absolute', left: 12, top: 12 }} />
                </div>
            )}

            {/* DRAFTS VIEW */}
            {activeTab === 'DRAFTS' ? (
                <div>
                    {!savedDraft ? (
                        <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 12, padding: '48px 20px', textAlign: 'center', color: 'var(--seller-muted, #64748b)', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                            <FileText size={44} style={{ opacity: 0.3, marginBottom: 12, color: '#FE2C55' }} />
                            <p style={{ margin: 0, fontSize: 14 }}>
                                {rtl ? 'لا توجد مسودات غير مكتملة محفوظة حالياً.' : 'No active drafts saved locally.'}
                            </p>
                        </div>
                    ) : (
                        <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 12, padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <div style={{ width: 56, height: 56, borderRadius: 10, background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FileEdit size={26} color="#FE2C55" />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <strong style={{ color: 'var(--seller-text, #111827)', fontSize: 15 }}>
                                            {savedDraft.nameAr || savedDraft.nameEn || (rtl ? 'مسودة منتج جديد' : 'Untitled Draft')}
                                        </strong>
                                        <span style={{ background: '#fff1f2', color: '#FE2C55', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                            LOCAL DRAFT
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                                        <span>{rtl ? 'القسم: ' : 'Category: '}<strong>{savedDraft.categoryName || 'Not selected'}</strong></span>
                                        <span>{rtl ? 'السعر: ' : 'Price: '}<strong style={{ color: '#FE2C55' }}>{savedDraft.price || '0'} {savedDraft.currency || 'SAR'}</strong></span>
                                        <span>{rtl ? 'الصور: ' : 'Images: '}<strong>{savedDraft.images?.length || 0}</strong></span>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Link 
                                    to="/seller/products/new?resume=true"
                                    style={{ background: 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)', color: '#fff', padding: '9px 18px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 2px 8px rgba(254,44,85,0.3)' }}
                                >
                                    {rtl ? 'متابعة التعديل والإرسال' : 'Resume Draft'}
                                </Link>
                                <button 
                                    onClick={handleDiscardDraft}
                                    style={{ background: 'transparent', border: '1px solid var(--seller-border, #e5e7eb)', color: '#ef4444', padding: '9px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                                >
                                    {rtl ? 'حذف المسودة' : 'Discard'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : loading ? (
                <div style={{ padding: '60px 0', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : filteredProducts.length === 0 ? (
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 12, padding: '48px 20px', textAlign: 'center', color: 'var(--seller-muted, #64748b)', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                    <Package size={44} style={{ opacity: 0.3, marginBottom: 12, color: '#FE2C55' }} />
                    <p style={{ margin: 0, fontSize: 14 }}>
                        {rtl ? 'لا توجد منتجات مطابقة لهذا التبويب.' : 'No products found matching this filter.'}
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {filteredProducts.map(prod => {
                        const img = prod.primaryImageUrl || prod.imageUrl || (prod.images && prod.images[0]?.url) || (prod.images && prod.images[0]) || '';
                        const isPending = prod.approvalStatus === 'PENDING' || prod.approvalStatus === 'PENDING_REVIEW' || (!prod.active && prod.approvalStatus !== 'REJECTED');
                        const isRejected = prod.approvalStatus === 'REJECTED';
                        const isApproved = prod.approvalStatus === 'APPROVED' || (prod.active && !isRejected && !isPending);

                        return (
                            <div 
                                key={prod.id}
                                style={{
                                    background: 'var(--seller-card, #ffffff)',
                                    border: `1px solid ${isRejected ? 'rgba(254,44,85,0.3)' : 'var(--seller-border, #e5e7eb)'}`,
                                    borderRadius: 12,
                                    padding: '18px 20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                        <div style={{ width: 64, height: 64, borderRadius: 10, background: '#f8f9fa', border: '1px solid #e5e7eb', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {img ? (
                                                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <Package size={26} color="#64748b" />
                                            )}
                                        </div>

                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                                <strong style={{ fontSize: 15, color: 'var(--seller-text, #111827)' }}>
                                                    {prod.nameAr || prod.name}
                                                </strong>
                                                {isApproved && <span className="status-badge approved">PUBLISHED</span>}
                                                {isPending && <span className="status-badge pending">PENDING REVIEW</span>}
                                                {isRejected && <span className="status-badge rejected">REJECTED</span>}
                                            </div>

                                            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                                                <span>{rtl ? 'القسم: ' : 'Category: '}<strong>{prod.categoryName || prod.category?.name || 'General'}</strong></span>
                                                <span>{rtl ? 'السعر: ' : 'Price: '}<strong style={{ color: '#FE2C55' }}>{prod.price} {prod.currency || 'SAR'}</strong></span>
                                                <span>{rtl ? 'المخزون: ' : 'Stock: '}<strong>{prod.quantity || 1}</strong></span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        {isApproved && (
                                            <Link 
                                                to={`/products/${prod.id}`} 
                                                target="_blank"
                                                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#ffffff', border: '1px solid var(--seller-border, #e5e7eb)', color: 'var(--seller-text, #111827)', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
                                            >
                                                <ExternalLink size={14} />
                                                <span>{rtl ? 'معاينة في المتجر' : 'View Live'}</span>
                                            </Link>
                                        )}

                                        <Link 
                                            to={`/seller/products/new?edit=${prod.id}`}
                                            style={{ display: 'flex', alignItems: 'center', gap: 6, background: isRejected ? 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)' : '#111827', color: '#fff', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none', boxShadow: isRejected ? '0 2px 8px rgba(254,44,85,0.3)' : 'none' }}
                                        >
                                            <FileEdit size={14} />
                                            <span>{isRejected ? (rtl ? 'تعديل وإعادة الإرسال' : 'Edit & Resubmit') : (rtl ? 'تعديل' : 'Edit')}</span>
                                        </Link>

                                        <button 
                                            onClick={() => handleDelete(prod.id)}
                                            style={{ background: 'transparent', border: '1px solid var(--seller-border, #e5e7eb)', color: 'var(--seller-muted, #9ca3af)', padding: 8, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </div>

                                {/* Rejection Reason Alert Card if Rejected */}
                                {isRejected && (
                                    <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <AlertCircle size={16} color="#FE2C55" style={{ flexShrink: 0 }} />
                                        <span style={{ fontSize: 12, color: '#9f1239' }}>
                                            <strong>{rtl ? 'سبب رفض الإدارة: ' : 'Admin Rejection Reason: '}</strong>
                                            {prod.rejectionReason || (rtl ? 'بيانات المنتج أو جودة الصور بحاجة للتصحيح.' : 'Product details require corrections.')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
